import {
  DateTimeFilter,
  MultiSelectFilter,
} from '@dataGrid/components/columnFilters';
import appConstants from "../../../../../../appConstants";
import CheckboxRenderer from "../../../components/CheckboxRenderer";
import { IBETARenderer } from '@dataGrid/components/cellRenderer';
import { format } from "date-fns";
import { checkInPlanShipmentsExistInBatch } from "../../utils";
import CheckBoxHeader from "./CheckboxHeader";
import { formatDateTime, mergeUserConfig } from '@dataGrid/gridUtilities';

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ terminal, userConfig}) {
    const defaultConfig = GridConfig.getDefaultConfiguration({ terminal });
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static isFirstColumn(params) {
    const displayedColumns = params.columnApi.getAllDisplayedColumns();
    return displayedColumns[0] === params.column;
  }

  static checkboxValueGetter(params) {
    return {
      checked: params.data.isSelected,
      isIntermediate: params.data.isIntermediate,
      disabled: checkInPlanShipmentsExistInBatch(
        params.data.shipmentsForDestTerminal
      ),
    };
  }

  static formatDateTime(params) {
    return params.value ? format(params.value, appConstants.DATETYPES.DATETIME.format) : "";
  }

  static getCurrentLocation(params) {
    return params.data.currentTerminal;
  }

  static getNextLocation(params) {
    return params.data.nextTerminal;
  }

  static getTrailerShipments(params){
    return params.data.shipmentsForDestTerminal
      .map((pro) => pro.proId)
      .join(",");
  }

  static getTrailerDestination(params){
    if (params.data.status === "Dock"){
      return ""
    } 
    return params.data.destTerminal
  }

  static getDefaultConfiguration({ terminal }) {
    return {
      title: appConstants.gridNames.IBbillsGrid,
      keyColumn: "id",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        resizable: true,
      },
      rowClassRules: {
        "ag-row-selected": (params) => params.data.isSelected,
        "ag-row-disabled": (params) =>
          checkInPlanShipmentsExistInBatch(
            params.data.shipmentsForDestTerminal
          ),
      },
      columns: [
        {
          headerName: "",
          valueGetter: GridConfig.checkboxValueGetter,
          colId: "batchCheckbox",
          headerClass: "checkBoxHeader",
          headerComponent: CheckBoxHeader,
          cellRenderer: CheckboxRenderer,
          minWidth: appConstants.columnWidth.W_62,
          maxWidth: appConstants.columnWidth.W_62,
          isInvisibleInShowHide: true,
          cellClass: "gridCell-centerAlign",
          cellClassRules: {
            disabledCheckboxIBBills: (params) =>
              checkInPlanShipmentsExistInBatch(
                params.data.shipmentsForDestTerminal
              ),
          },
        },
        {
          headerName: "Trailer #",
          field: "trailerNumber",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_100,
        },
        {
          headerName: "SCAC Code",
          headerTooltip: "SCAC Code",
          field: "trailerScac",
          hide: true,
          filter: MultiSelectFilter,
          width: appConstants.columnWidth.W_120,
          minWidth: appConstants.columnWidth.W_120,
          cellClass: "gridCell-leftAlign",
        },
        {
          headerName: "Manifest",
          field: "manifestNumber",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
          hide : true,
        },
        {
          headerName: "Current Status",
          headerTooltip: "Current Status",
          field: "status",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_140,
        },
        {
          headerName: "Current Loc",
          headerTooltip: "Current Location",
          field: "currentTerminal",
          valueGetter: GridConfig.getCurrentLocation,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Next Loc",
          headerTooltip: "Next Location",
          field: "nextTerminal",
          valueGetter: GridConfig.getNextLocation,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Trailer Orig",
          headerTooltip: "Trailer Origin",
          field: "originTerminal",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Trailer Dest",
          headerTooltip: "Trailer Destination",
          field: "destTerminal",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
          valueGetter: GridConfig.getTrailerDestination,
        },
        {
          headerName: "Run#",
          headerTooltip: "Run Number",
          field: "runNumber",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_80,
        },
        {
          headerName: "Driver",
          headerTooltip: "Driver",
          field: "driverName",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_180,
        },
        {
          headerName: `${terminal} Bills`,
          field: "shipmentsForDestTerminalCount",
          cellClass: "actionable-cell",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_120,
          colId: "totalBills",
        },
        {
          headerName: `Ttl Trlr Bills`,
          headerTooltip: "Total Trailer Bills",
          field: "shipmentsInTrailerCount",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_120,
        },
        {
          headerName: "Calculated ETA",
          headerTooltip: "Calculated ETA",
          field: "minIBETA",
          minWidth: appConstants.columnWidth.IBETA,
          valueFormatter: GridConfig.formatDateTime,
          cellRenderer: IBETARenderer,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "SVC",
          headerTooltip: "SVC",
          field: "priorityBillsCount",
          cellClass: "gridCell-leftAlign actionable-cell",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
          colId : "svc"
        },
        {
          headerName: `${terminal} Bills Due Today`,
          field: "shipmentsDueOnPlanDateCount",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_170,
          cellClass: "actionable-cell",
          colId: "totalBillsDueToday",
        },
        {
          headerName: "Ttl Trlr HU",
          headerTooltip: "Total Trailer HU",
          field: "totalTrailerHu",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_140,
        },
        {
          headerName: "Ttl Trlr Wgt",
          headerTooltip : "Total Trailer Weight",
          field: "totalTrailerWeight",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_140,
        },
        {
          headerName: "Last Updated",
          headerTooltip: "Last Updated",
          field: "lastUpdated",
          minWidth: appConstants.columnWidth.W_150,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          valueFormatter: formatDateTime,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.DATETIME,
          }
        },
        {
          headerName: "Trailer Shipments",   
          hide : true,   //hiding it as this column is added only to allow user to search by pro numbers using trailers grid search 
          isInvisibleInShowHide: true,
          valueGetter:GridConfig.getTrailerShipments,
          minWidth: appConstants.columnWidth.W_140,
        }
      ],
    };
  }
}
